/**
 * Passphrase Canonicalization for Key Derivation
 *
 * Transforms user passphrases into canonical form for cryptographic operations.
 * This module is critical for security: the canonical string is used for local
 * Argon2 key derivation and MUST NEVER be transmitted to servers.
 *
 * SECURITY NOTICE:
 * The canonical form strips FILLER tokens (connectors like "and", "y", "the")
 * to maximize entropy density while maintaining deterministic key generation.
 * This allows users to create memorable passphrases while deriving strong keys.
 */

import { tokenizeAndClassify, Locale } from './classifyToken';

/**
 * Convert a passphrase into canonical form for key derivation.
 *
 * SECURITY: Never send this canonical string to the server. Use only for
 * local Argon2/AES-GCM key derivation.
 *
 * The canonicalization process:
 * 1. Tokenize and classify the passphrase
 * 2. Filter out FILLER tokens (connectors that add no entropy)
 * 3. Transform remaining tokens:
 *    - WORD: lowercase and trim whitespace
 *    - EMOJI: preserve as-is in NFC normalization
 *    - DIGIT: preserve as-is
 *    - CHAR: preserve as-is
 * 4. Join with '|' separator for deterministic reconstruction
 *
 * @param passphrase - The user's input passphrase
 * @param locale - Language locale for filler detection
 * @returns Canonical string suitable for key derivation
 *
 * @example
 * canonicalizeForKey('my cat 🐈 runs', 'en')
 * // 'my|cat|🐈|runs'
 *
 * canonicalizeForKey('👦🏼-🐈-SE-🗣️-BRULA-Y-✈️', 'es')
 * // '👦🏼|🐈|🗣️|brula|✈️'
 * // (SE and Y are Spanish fillers, removed)
 *
 * canonicalizeForKey('The Dog is Named Max', 'en')
 * // 'dog|named|max'
 * // (The and is are English fillers, removed)
 */
export function canonicalizeForKey(passphrase: string, locale: Locale): string {
  // Tokenize and classify the passphrase
  const classified = tokenizeAndClassify(passphrase, locale);

  // Filter out FILLER tokens and transform the rest
  const canonicalTokens = classified
    .filter(({ type }) => type !== 'FILLER')
    .map(({ value, type }) => {
      switch (type) {
        case 'WORD':
          // Lowercase words for case-insensitive matching
          return value.toLowerCase().trim();

        case 'EMOJI':
        case 'DIGIT':
        case 'CHAR':
          // Preserve emoji, digits, and special chars as-is
          // (already in NFC form from tokenization)
          return value;

        default:
          return value;
      }
    });

  // Join with pipe separator for deterministic reconstruction
  return canonicalTokens.join('|');
}
